import { Suspense } from "react";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import dynamic from "next/dynamic";
import { Meta, appEnv, publicConfig } from "@lululemon/ecom-core";
import { ErrorBoundary } from "@lululemon/dsp-shared";
import { Footer } from "@lululemon/footer";
import { NavDataProvider, Meganav } from "@lululemon/meganav";

import { LinkProvider } from "@lululemon/dsp-ecom-pattern-library";
import { useRegionalAccuracy } from "@lululemon/mwa-i18n";
import { useHeaderFlags } from "@lululemon/mwa-header-flags";
import {
  useLoadGoogleMapsKey,
  LinkProviderModifier
} from "@lululemon/dsp-utils";

const Error = dynamic(() => import("./error"));
const RegionalBanner = dynamic(() =>
  import("@lululemon/mwa-seo").then((mod) => mod.RegionalBanner)
);

function Layout({ children, serverProps }) {
  const { navState, regionalAccuracyContentfulConfig, googleMapsApiKey } =
    serverProps;

  useLoadGoogleMapsKey(googleMapsApiKey);

  const consumerConfig = {
    apiHost: publicConfig.nav.apiHost,
    apiHostBasePath: publicConfig.nav.apiHostBasePath,
    googleMapsApiKey: publicConfig.googleMapsApiKey,
    launchDarkly: { clientId: publicConfig?.launchDarkly?.navClientSideId },
    links: publicConfig.nav.links
  };

  const { locale, asPath } = useRouter();
  const handleError = (error, errorInfo) => {
    if (appEnv.isDev()) {
      console.error("stores-mfe error", error, errorInfo);
    }
  };

  return (
    <LinkProvider component={LinkProviderModifier}>
      <Meta />
      <NavDataProvider navData={navState}>
        <Meganav consumerConfig={consumerConfig} />
        <ErrorBoundary onError={handleError} fallback={<Error />}>
          <Suspense fallback={null}>
            {locale ? (
              <RegionalBanner
                locale={locale}
                currentPath={asPath}
                contentfulConfig={regionalAccuracyContentfulConfig}
              />
            ) : null}
          </Suspense>
        </ErrorBoundary>
        <ErrorBoundary fatal renderFallback={() => <Error />}>
          {children}
        </ErrorBoundary>
        <Footer />
      </NavDataProvider>
    </LinkProvider>
  );
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  navData: PropTypes.shape({
    footer: PropTypes.object,
    nav: PropTypes.object,
    personal: PropTypes.object,
    navConfig: PropTypes.object
  })
};

Layout.defaultProps = {
  navData: {}
};

export default Layout;
